import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "./redux-hooks";
import axios, {AxiosError} from 'axios'
import * as Sentry from "@sentry/react";
import { 
  faServer,
  faGlobe,
  faWifi,
  faShieldHalved,
  faIdCard
} from '@fortawesome/free-solid-svg-icons';
import { updateSystemStatus } from "src/store/slices/systemPrecheckSlice";
import { useToast } from "src/contexts/ToastProvider";

const baseUrl = process.env.REACT_APP_API_BASE_URL ?? '';
const healthyCheckUrl = baseUrl.substring(0, baseUrl.indexOf('/api/')) + '/health';

export const useSystemPrecheck = ()=> {
  const toast = useToast();
  const overallSystemPreCheckStatus = useAppSelector(
    state => state.systemPrecheck.overallSystemPreCheckStatus)

  const dispatch = useAppDispatch()
  const [checks, setChecks] = useState<SystemCheck[]>([
    { id: 'api', name: 'API Connection', status: 'pending', message: '', icon: faServer },
    { id: 'regula', name: 'Document Verification Service', status: 'pending', message: '', icon: faIdCard },
    { id: 'browser', name: 'Browser Compatibility', status: 'pending', message: '', icon: faGlobe },
    { id: 'network', name: 'Network Status', status: 'pending', message: '', icon: faWifi },
    { id: 'permissions', name: 'Required Permissions', status: 'pending', message: '', icon: faShieldHalved }
  ]);

  const [showPrecheck, setShowPrecheck] = useState<boolean>(false);
  
  const updateCheck = useCallback((id: string, status: SystemPreCheckStatus, message: string = ''): void => {
    setChecks(prev => prev.map(check => 
      check.id === id ? { ...check, status, message } : check
    ));
    
    if(status === 'warning'){
      const name = checks?.find(check => check?.id === id)?.name ?? ""
      toast.warn(message,{title:name,autoClose:80000})
      
      // Log warning to Sentry
      Sentry.captureMessage(`System Precheck Warning: ${name}`, {
        level: 'warning',
        tags: {
          check_type: id,
          check_name: name,
        },
        extra: {
          message,
          status,
        },
      });
    } 
    if(status === 'error'){
      const name = checks?.find(check => check?.id === id)?.name ?? ""
      toast.error(message,{title:name,autoClose:60000})
      
      // Log error to Sentry
      Sentry.captureMessage(`System Precheck Error: ${name}`, {
        level: 'error',
        tags: {
          check_type: id,
          check_name: name,
        },
        extra: {
          message,
          status,
        },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkBrowserCompatibility = useCallback(async (): Promise<void> => {
    try {
      const isCompatible = 
        'fetch' in window &&
        'Promise' in window;

      
      if (isCompatible) {
        updateCheck('browser', 'success', 'Browser fully supported');
      } else {
        updateCheck('browser', 'error', 'Your browser may not be fully compatible and can affect your ability to use this application.');
      }
    } catch (err) {
      const errorMessage = 'We were unable to verify browser compatibility and this can affect your ability to use this application.';
      
      Sentry.captureException(err, {
        tags: {
          check_type: 'browser',
          check_name: 'Browser Compatibility',
        },
        extra: {
          message: errorMessage,
        },
      });
      
      updateCheck('browser', 'error', errorMessage);
    }
  }, [updateCheck]);

  const getWebRTCIPs = (): Promise<string[]> => {
    return new Promise((resolve) => {
      const ips: Set<string> = new Set();
      const timeout = setTimeout(() => resolve(Array.from(ips)), 1000);

      try {
        const pc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        });

        pc.createDataChannel('');
        
        pc.onicecandidate = (event) => {
          if (!event.candidate) {
            clearTimeout(timeout);
            pc.close();
            resolve(Array.from(ips));
            return;
          }

          const candidate = event.candidate.candidate;
          const ipRegex = /(\d{1,3}\.){3}\d{1,3}/;
          const match = candidate.match(ipRegex);
          
          if (match && !match[0].startsWith('0.') && !match[0].startsWith('127.')) {
            ips.add(match[0]);
          }
        };

        pc.createOffer()
          .then(offer => pc.setLocalDescription(offer))
          .catch(() => {
            clearTimeout(timeout);
            pc.close();
            resolve(Array.from(ips));
          });
      } catch (err) {
        clearTimeout(timeout);
        resolve(Array.from(ips));
      }
    });
  };

  const measureDNSTime = useCallback(async (): Promise<number> => {
    const start = performance.now();
    try {
      await fetch('https://www.google.com/favicon.ico', { 
        method: 'HEAD',
        cache: 'no-cache',
        mode: 'no-cors'
      });
      return performance.now() - start;
    } catch (err) {
      return 0;
    }
  },[]);

  const checkConnectionSpeed = useCallback(async (): Promise<{ speed: number; quality: 'fast' | 'moderate' | 'slow' }> => {
    const testImageUrl = 'https://via.placeholder.com/1000x1000'; // ~1MB test file
    const startTime = performance.now();
    
    try {
      const response = await fetch(testImageUrl + '?t=' + Date.now(), { 
        cache: 'no-cache' 
      });
      const blob = await response.blob();
      const endTime = performance.now();
      
      const durationInSeconds = (endTime - startTime) / 1000;
      const fileSizeInMB = blob.size / (1024 * 1024);
      const speedMbps = (fileSizeInMB * 8) / durationInSeconds;
      
      let quality: 'fast' | 'moderate' | 'slow';
      if (speedMbps > 5) quality = 'fast';
      else if (speedMbps > 1) quality = 'moderate';
      else quality = 'slow';
      
      return { speed: speedMbps, quality };
    } catch (err) {
      console.error('Speed test failed:', err);
      return { speed: 0, quality: 'slow' };
    }
  }, []);

  const checkUploadSpeed = useCallback(async (): Promise<{ speed: number; quality: 'fast' | 'moderate' | 'slow' }> => {
    try {
      // Create a ~500KB blob to upload (simulating a document image)
      const testData = new Blob([new ArrayBuffer(512 * 1024)], { type: 'application/octet-stream' });
      const formData = new FormData();
      formData.append('file', testData, 'test.dat');
      
      const startTime = performance.now();

      // Use httpbin upload test endpoint
      await fetch('https://httpbin.org/post', {
        method: 'POST',
        body: formData,
        cache: 'no-cache'
      });
      
      const endTime = performance.now();
      const durationInSeconds = (endTime - startTime) / 1000;
      const fileSizeInMB = testData.size / (1024 * 1024);
      const speedMbps = (fileSizeInMB * 8) / durationInSeconds;
      
      let quality: 'fast' | 'moderate' | 'slow';
      if (speedMbps > 2) quality = 'fast';
      else if (speedMbps > 0.5) quality = 'moderate';
      else quality = 'slow';
      
      return { speed: speedMbps, quality };
    } catch (err) {
      console.error('Upload speed test failed:', err);
      return { speed: 0, quality: 'slow' };
    }
  }, []);

  const checkConnectionStability = useCallback(async (): Promise<{ stable: boolean; avgLatency: number; jitter: number }> => {
    const pingCount = 5;
    const latencies: number[] = [];
    
  
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;
    const connectionType = connection?.effectiveType || 'unknown';
    
    for (let i = 0; i < pingCount; i++) {
      const start = performance.now();
      try {
        await fetch('https://www.google.com/favicon.ico', {
          method: 'HEAD',
          cache: 'no-cache',
          mode: 'no-cors'
        });
        latencies.push(performance.now() - start);
      } catch {
        latencies.push(0);
      }
      await new Promise(resolve => setTimeout(resolve, 500)); 
    }
    
    const validLatencies = latencies.filter(l => l > 0);
    if (validLatencies.length === 0) {
      return { stable: false, avgLatency: 0, jitter: 0 };
    }
    
    const avgLatency = validLatencies.reduce((a, b) => a + b, 0) / validLatencies.length;
    const variance = validLatencies.reduce((sum, lat) => sum + Math.pow(lat - avgLatency, 2), 0) / validLatencies.length;
    const jitter = Math.sqrt(variance);
    

    let jitterThreshold = 50;
    let latencyThreshold = 500;
    

    if (['slow-2g', '2g'].includes(connectionType)) {
      jitterThreshold = 200;
      latencyThreshold = 2000;
    } else if (['3g'].includes(connectionType)) {
      jitterThreshold = 150;
      latencyThreshold = 1500;
    } else if (['4g'].includes(connectionType)) {
      jitterThreshold = 100;
      latencyThreshold = 1000;
    }
    
    const stable = jitter < jitterThreshold && avgLatency < latencyThreshold;
    
    return { stable, avgLatency, jitter };
  }, []);

  const detectVPNOrProxy = useCallback(async (): Promise<{ detected: boolean; indicators: string[] }> => {
    const indicators: string[] = [];

    try {

      const connection = (navigator as any).connection || 
                        (navigator as any).mozConnection || 
                        (navigator as any).webkitConnection;
      const isMobileConnection = connection && 
        ['slow-2g', '2g', '3g', '4g'].includes(connection.effectiveType);

      // 1. Check for multiple IPs 
      const rtcIPs = await getWebRTCIPs();
      // only flag if:
      // - More than 2 IPs detected, OR
      // - Multiple IPs detected AND not on a mobile connection
      if (rtcIPs.length > 2 || (rtcIPs.length > 1 && !isMobileConnection)) {
        indicators.push('Multiple IPs detected');
      }

      // 2. Check timezone vs geolocation mismatch
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        // Get approximate location from IP using a geolocation API
        const geoResponse = await fetch('https://ipapi.co/json/', {
          method: 'GET',
          cache: 'no-cache'
        });
        
        if (geoResponse.ok) {
          const geoData = await geoResponse.json();
          const detectedTimezone = geoData.timezone;
          
          // Compare browser timezone with IP-based timezone
          if (timezone && detectedTimezone && timezone !== detectedTimezone) {
            indicators.push(`Timezone mismatch (Browser: ${timezone}, IP: ${detectedTimezone})`);
          }
        }
      } catch (tzErr) {
        console.log('Timezone check failed:', tzErr);
      }

      // 3. Check for common VPN/Proxy headers via a test request
      try {
        const response = await fetch('https://httpbin.org/headers', {
          method: 'GET',
          cache: 'no-cache'
        });
        const data = await response.json();
        const headers = data.headers || {};

        // Check for proxy headers
        if (headers['X-Forwarded-For'] || 
            headers['X-Forwarded-Host'] || 
            headers['Via'] || 
            headers['X-Real-Ip']) {
          indicators.push('Proxy headers detected');
        }
      } catch (headerErr) {
        console.log('Header check failed:', headerErr);
      }

      // 4. Check DNS resolution time
      try {
        const dnsTime = await measureDNSTime();
        if (dnsTime > 500) {
          indicators.push('Slow DNS resolution');
        }
      } catch (dnsErr) {
        console.log('DNS check failed:', dnsErr);
      }

      return {
        detected: indicators.length >= 2, 
        indicators
      };
    } catch (err) {
      console.error('VPN/Proxy detection failed:', err);
      return { detected: false, indicators: [] };
    }
  }, [measureDNSTime]);

const checkNetworkStatus = useCallback(async (): Promise<void> => {
  try {
    if (!navigator.onLine) {
      updateCheck('network', 'error', 'No internet connection and can affect your ability to use this application');
      return;
    }

    // Check download speed
    const speedTest = await checkConnectionSpeed();
    
    // Check upload speed
    const uploadTest = await checkUploadSpeed();
    
    // Check connection stability
    const stabilityTest = await checkConnectionStability();
    
    // Check for VPN/Proxy indicators
    const vpnIndicators = await detectVPNOrProxy();
    
    // Get network connection info
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;
    
    // Build status message
    let statusParts: string[] = [];
    let overallStatus: SystemPreCheckStatus = 'success';
    
    // Download speed assessment - ONLY show if slow
    if (speedTest.speed > 0 && speedTest.quality === 'slow') {
      statusParts.push(`Download speed may be slow`);
      overallStatus = 'warning';
    }
    
    // Upload speed assessment - ONLY show if slow
    if (uploadTest.speed > 0 && uploadTest.quality === 'slow') {
      statusParts.push(`Upload speed may be slow`);
      overallStatus = 'warning';
    }
    
    // Stability assessment - ONLY show if unstable
    if (!stabilityTest.stable) {
      statusParts.push(`Connection appears unstable`);
      overallStatus = 'warning';
    }
    
    // VPN/Proxy detection - ONLY show if detected
    if (vpnIndicators.detected) {
      statusParts.push(`VPN or proxy detected`);
      if (overallStatus === 'success') overallStatus = 'warning';
    }
    
    // Connection type - ONLY show for slow mobile connections (2G, 3G) that might cause issues
    if (connection && connection.effectiveType && ['slow-2g', '2g', '3g'].includes(connection.effectiveType)) {
      statusParts.push(`Mobile connection (${connection.effectiveType.toUpperCase()})`);
      if (overallStatus === 'success') overallStatus = 'warning';
    }
    

    const message = statusParts.length > 0 
      ? statusParts.join(' • ') + ' - can affect your ability to use this application'
      : 'Connection is working well';
    updateCheck('network', overallStatus, message);
    
  } catch (err) {
    const errorMessage = 'Unable to check connection quality and can affect your ability to use this application';
    console.error('Network check failed:', err);
    
    Sentry.captureException(err, {
      tags: {
        check_type: 'network',
        check_name: 'Network Status',
      },
      extra: {
        message: errorMessage,
      },
    });
    
    updateCheck('network', 'warning', errorMessage);
  }
}, [checkConnectionSpeed, checkUploadSpeed, checkConnectionStability, detectVPNOrProxy, updateCheck]);


const checkAPIConnection = useCallback(async (): Promise<void> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const {data} = await axios.get(healthyCheckUrl, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (data) {
        updateCheck('api', 'success', 'System is ready');
      } else {
        updateCheck('api', 'error', 'Unable to connect to system and can affect your ability to use this application');
      }
    } catch (err) {
      if(axios.isAxiosError(err)){
        const error = err as AxiosError
        
        if(error.status === 404){
          return updateCheck('api', 'success', 'System is ready');
        }
        
        let errorMessage = 'Unable to connect to system and can affect your ability to use this application';
        if (axios.isCancel(err)) {
          errorMessage = 'Connection timeout - please check your internet as this can affect your ability to use this application';
        }
        
        Sentry.captureException(err, {
          tags: {
            check_type: 'api',
            check_name: 'API Connection',
          },
          extra: {
            message: errorMessage,
            status: error.status,
            url: healthyCheckUrl,
          },
        });
        
        updateCheck('api', 'error', errorMessage);
      }
    }
  }, [updateCheck]);

  const checkRegulaConnection = useCallback(async (): Promise<void> => {
    const regulaEndpoints = [
      'https://lic.regulaforensics.com/healthcheck',
      'https://lic2.regulaforensics.com/healthcheck'
    ];

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const results = await Promise.allSettled(
        regulaEndpoints.map(url => 
          axios.get(url, {
            signal: controller.signal,
            timeout: 10000
          })
        )
      );

      clearTimeout(timeoutId);

      // Check if at least one regula license service endpoint is healthy
      const successfulResults = results.filter(result => 
        result.status === 'fulfilled' && result.value.status === 200
      );

      if (successfulResults.length > 0) {
        updateCheck('regula', 'success', 'Document verification service is ready');
      } else {
        // Check if any endpoint returned a response (even if not 200)
        const anyResponse = results.some(result => result.status === 'fulfilled');
        
        if (anyResponse) {
          updateCheck('regula', 'warning', 'Document verification service may be experiencing issues and can affect your ability to use this application');
        } else {
          updateCheck('regula', 'error', 'Unable to connect to document verification service and can affect your ability to use this application');
        }
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const error = err as AxiosError;
        
        let errorMessage = 'Unable to connect to document verification service and can affect your ability to use this application';
        if (axios.isCancel(err)) {
          errorMessage = 'Document verification service connection timeout - please check your internet as this can affect your ability to use this application';
        }
        
        Sentry.captureException(err, {
          tags: {
            check_type: 'regula',
            check_name: 'Document Verification Service',
          },
          extra: {
            message: errorMessage,
            status: error.status,
            endpoints: regulaEndpoints,
          },
        });
        
        updateCheck('regula', 'error', errorMessage);
      }
    }
  }, [updateCheck]);

  const testCameraAccess = useCallback(async (): Promise<void> => {
    let stream: MediaStream | null = null;
    try {
      // Request camera access
      stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });

      updateCheck('permissions', 'success', 'Camera access granted.');
    } finally {
      //  release camera device
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    }
  }, [updateCheck]);

  const checkCameraPermissions = useCallback(async (): Promise<void> => {
    try {
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        updateCheck(
          'permissions',
          'warning',
          'Camera API not supported and can affect your ability to use this application.'
        );
        return;
      }

      // Check camera permission state
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const permissionStatus = await navigator.permissions.query({
            name: 'camera' as PermissionName,
          });

          if (permissionStatus.state === 'denied') {
            updateCheck(
              'permissions',
              'warning',
              'Camera access denied and can affect your ability to use this application.'
            );
            return;
          }

          if (permissionStatus.state === 'granted') {
            await testCameraAccess();
            return;
          }
        } catch {
          console.log('Permissions API query failed, testing direct access.');
        }
      }

      // If permission state is prompt or unknown, test direct access
      await testCameraAccess();
    } catch (err) {
      let errorMessage = 'Unknown camera error and can affect your ability to use this application.';
      
      if (err instanceof Error) {
        switch (err.name) {
          case 'NotAllowedError':
          case 'PermissionDeniedError':
            errorMessage = 'Camera access denied by user and can affect your ability to use this application.';
            break;
          case 'NotFoundError':
            errorMessage = 'No camera detected and can affect your ability to use this application.';
            break;
          case 'NotReadableError':
            errorMessage = 'Camera currently in use and can affect your ability to use this application.';
            break;
          default:
            errorMessage = `Camera error: ${err.message} and can affect your ability to use this application.`;
            break;
        }
      }
      
      Sentry.captureException(err, {
        tags: {
          check_type: 'permissions',
          check_name: 'Camera Permissions',
        },
        extra: {
          message: errorMessage,
          errorName: err instanceof Error ? err.name : 'Unknown',
        },
      });
      
      updateCheck('permissions', 'warning', errorMessage);
    }
  }, [testCameraAccess, updateCheck]);

  const checkPermissions = useCallback(async (): Promise<void> => {
    await checkCameraPermissions()
  }, [checkCameraPermissions]);

  const finalizeChecks = useCallback((): void => {
    setChecks(prev => {
      const hasErrors = prev.some(c => c.status === 'error');
      const hasWarnings = prev.some(c => c.status === 'warning');
      
      if (hasErrors) {
        dispatch(updateSystemStatus('failed'))
        setShowPrecheck(true); // Only show if there are errors
      } else if (hasWarnings) {
        dispatch(updateSystemStatus("warning"))
        setShowPrecheck(true); // Only show if there are warnings
      } else {
        dispatch(updateSystemStatus('success'))
        // Don't show precheck if everything is successful
      }
      
      return prev;
    });
  }, [dispatch]);

  const handleContinue = (): void => {
    if (overallSystemPreCheckStatus === 'success' || overallSystemPreCheckStatus === 'warning') {
      setShowPrecheck(false);
    }
  };

  const runSystemChecks = useCallback(async (): Promise<void> => {
    // Browser Compatibility
    await checkBrowserCompatibility();

    // Network Status 
    await checkNetworkStatus();

    // API Connection
    await checkAPIConnection();

    // Regula Document Verification Service
    await checkRegulaConnection();

    // Permissions
    await checkPermissions();
    
    // Finalize
    finalizeChecks();
  }, [checkBrowserCompatibility, checkNetworkStatus, checkAPIConnection, checkRegulaConnection, checkPermissions, finalizeChecks]);

  useEffect(() => {
    runSystemChecks();
  }, [runSystemChecks]);

  return {checks, showPrecheck, handleContinue}
}